/**
 * Page de journal de consommation des crédits
 * Affiche l'historique détaillé de toutes les consommations de crédits
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { isPaidPlanType } from "@core/constants/quotas";
import {
  getConsumptionJournal,
  type CreditJournalEntry,
} from "@core/lib/quotaTracking";
import {
  getGuestQuotaJournal,
  type GuestQuotaJournalEntry,
} from "@core/lib/guestQuotaService";
import {
  getConversations as getSupabaseConversations,
  getConversationsByGuest,
} from "@core/lib/storage/ConversationStorageSupabase";
import { pollService } from "@core/services/PollService";
import { getFingerprint } from "@core/lib/guestIdentityService";
import { type Conversation } from "@core/types/conversation";
import { useFreemiumQuota } from "@core/hooks/useFreemiumQuota";
import {
  ArrowLeft,
  Calendar,
  CreditCard,
  MessageSquare,
  BarChart3,
  Zap,
  FileText,
  Search,
} from "lucide-react";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@core/components/ui/tooltip";
import { getThemeColors } from "../products/dashboard/utils";
import { ContentTypeFilter } from "../products/dashboard/types";
import { PollLink } from "./PollLink";

type TFunc = (key: string, options?: Record<string, unknown>) => string;

const buildActionLabels = (
  t: TFunc,
): Record<
  string,
  { label: string; icon: React.ReactNode; color: string; quotaType: string }
> => ({
  conversation_created: {
    label: t("consumption_journal.action.conversation_created"),
    icon: <MessageSquare className="w-4 h-4" />,
    color: "text-blue-400",
    quotaType: "conversations",
  },
  poll_created: {
    label: t("consumption_journal.action.poll_created"),
    icon: <FileText className="w-4 h-4" />,
    color: "text-green-400",
    quotaType: "polls",
  },
  ai_message: {
    label: t("consumption_journal.action.ai_message"),
    icon: <Zap className="w-4 h-4" />,
    color: "text-purple-400",
    quotaType: "aiMessages",
  },
  analytics_query: {
    label: t("consumption_journal.action.analytics_query"),
    icon: <BarChart3 className="w-4 h-4" />,
    color: "text-orange-400",
    quotaType: "analytics",
  },
  simulation: {
    label: t("consumption_journal.action.simulation"),
    icon: <Zap className="w-4 h-4" />,
    color: "text-pink-400",
    quotaType: "analytics",
  },
  other: {
    label: t("consumption_journal.action.other"),
    icon: <CreditCard className="w-4 h-4" />,
    color: "text-gray-400",
    quotaType: "other",
  },
});

// Icônes spécifiques par type de poll
const POLL_TYPE_ICONS: Record<string, React.ReactNode> = {
  date: <Calendar className="w-4 h-4" />,
  form: <FileText className="w-4 h-4" />,
  quizz: <BarChart3 className="w-4 h-4" />,
  availability: <Calendar className="w-4 h-4" />,
};

const buildPollTypeLabels = (t: TFunc): Record<string, string> => ({
  date: t("consumption_journal.poll_type.date"),
  form: t("consumption_journal.poll_type.form"),
  quizz: t("consumption_journal.poll_type.quizz"),
  availability: t("consumption_journal.poll_type.availability"),
});

export default function ConsumptionJournal() {
  const { t } = useTranslation("polls");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { limits } = useFreemiumQuota();
  // Tout plan payant (solo ou dooquest_pro) = affichage "illimité" dans le journal.
  const isSolo = isPaidPlanType(profile?.plan_type);
  const isFree = !!user && !isSolo;

  const ACTION_LABELS = useMemo(() => buildActionLabels(t), [t]);
  const POLL_TYPE_LABELS = useMemo(() => buildPollTypeLabels(t), [t]);

  // Déterminer le thème basé sur l'URL
  const theme = (() => {
    let type: ContentTypeFilter = "date"; // default
    if (location.pathname.includes("/form/")) {
      type = "form";
    } else if (location.pathname.includes("/availability/")) {
      type = "availability";
    }
    return getThemeColors(type);
  })();

  const handleBack = () => {
    // Navigation simple vers le dashboard sans préfixe
    navigate("/dashboard");
  };
  const [journal, setJournal] = useState<CreditJournalEntry[]>([]);
  const [filteredJournal, setFilteredJournal] = useState<CreditJournalEntry[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<
    "current" | "all" | string
  >("current");
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  // Helper pour obtenir les informations de renouvellement
  const getResetInfo = useCallback(() => {
    const now = new Date();
    const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const daysUntilReset = Math.ceil(
      (resetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    let daysText = "";
    if (daysUntilReset === 0) daysText = t("consumption_journal.reset.today");
    else if (daysUntilReset === 1)
      daysText = t("consumption_journal.reset.tomorrow");
    else
      daysText = t("consumption_journal.reset.in_days", {
        count: daysUntilReset,
      });

    const resetDateFormatted = resetDate.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    return {
      daysUntilReset,
      daysText,
      resetDate: resetDateFormatted,
      nextMonthCredits: isSolo
        ? "∞"
        : isFree
          ? t("consumption_journal.lifetime")
          : "—",
    };
  }, [isSolo, isFree, t]);

  // Build journal entries.
  // Source de vérité : Supabase (polls.creator_id / conversations.user_id
  // pour les authentifiés, polls.guest_fingerprint pour les guests).
  // localStorage n'est plus lu — cf. Docs/ARCHITECTURE/2026-04-01-STORAGE-ARCHITECTURE-FINAL.md
  const createJournal = async (): Promise<CreditJournalEntry[]> => {
    const entries: CreditJournalEntry[] = [];
    const isCopyPoll = (slug: string) => /-copy-\d+$/.test(slug || "");

    try {
      // --- 1) Polls depuis Supabase ---
      const pollsFromDb = user
        ? await pollService.getPollsByUser(user.id)
        : await pollService.getPollsByGuest(await getFingerprint());

      const originalPolls = (pollsFromDb || []).filter(
        (poll: any) => !isCopyPoll(poll.slug || ""),
      );
      originalPolls.forEach((poll: any, index: number) => {
        const pollType = poll.type || "date";
        const pollTypeLabel =
          POLL_TYPE_LABELS[pollType] || t("consumption_journal.poll_fallback");
        const createdAt =
          poll.created_at || new Date(Date.now() - index * 60000).toISOString();

        entries.push({
          id: `db-poll-${poll.id}`,
          action: "poll_created",
          credits: 1,
          timestamp: createdAt,
          userId: user?.id ?? "guest",
          metadata: {
            pollId: poll.id,
            pollType,
            pollTitle: poll.title,
            pollTypeLabel,
            displayType: "poll",
          },
        });
      });

      // --- 2) Conversations ---
      // Authentifié : table `conversations` (user_id = user.id)
      // Guest : table `conversations` (guest_fingerprint)
      let conversations: Conversation[] = [];
      try {
        conversations = user
          ? await getSupabaseConversations(user.id)
          : await getConversationsByGuest(await getFingerprint());
      } catch (err) {
        logError(
          ErrorFactory.critical(
            "Failed to fetch conversations from Supabase",
            "JournalFetchError",
          ),
          {
            component: "ConsumptionJournal",
            metadata: { originalError: err },
          },
        );
      }

      conversations.forEach((conversation, index) => {
        const createdAt =
          (conversation as { created_at?: string }).created_at ||
          new Date(
            Date.now() - (originalPolls.length + index) * 60000,
          ).toISOString();

        entries.push({
          id: `conv-${conversation.id}`,
          action: "conversation_created",
          credits: 1,
          timestamp: createdAt,
          userId: user?.id ?? "guest",
          metadata: {
            conversationId: conversation.id,
            messageCount: conversation.messages?.length || 0,
            displayType: "conversation",
          },
        });
      });
    } catch (error) {
      logError(
        ErrorFactory.critical(
          "Failed to build consumption journal",
          "JournalBuildError",
        ),
        { component: "ConsumptionJournal", metadata: { originalError: error } },
      );
    }

    return entries;
  };

  useEffect(() => {
    const loadJournal = async () => {
      try {
        // Utiliser directement les données locales pour éviter les timeouts Supabase
        const entries = await createJournal();

        // Filtrer les entrées selon le type de produit (basé sur l'URL)
        // Utiliser window.location.pathname car location.pathname ne contient pas le basename
        const fullPath = window.location.pathname;
        let filteredEntries = entries;
        if (fullPath.includes("/date/")) {
          // Journal des date polls : afficher uniquement les sondages de dates
          filteredEntries = entries.filter((entry) => {
            // Garder toutes les entrées sauf poll_created avec un type différent de date
            if (entry.action === "poll_created" && entry.metadata?.pollType) {
              return entry.metadata.pollType === "date";
            }
            return true; // Garder les autres types d'actions (conversations, messages IA, etc.)
          });
        } else if (fullPath.includes("/form/")) {
          // Journal des form polls : afficher uniquement les sondages de formulaires
          filteredEntries = entries.filter((entry) => {
            if (entry.action === "poll_created" && entry.metadata?.pollType) {
              return entry.metadata.pollType === "form";
            }
            return true;
          });
        } else if (fullPath.includes("/availability/")) {
          // Journal des availability polls : afficher uniquement les sondages de disponibilité
          filteredEntries = entries.filter((entry) => {
            if (entry.action === "poll_created" && entry.metadata?.pollType) {
              return entry.metadata.pollType === "availability";
            }
            return true;
          });
        } else if (fullPath.includes("/quizz-polls/")) {
          // Journal des quizz polls : afficher uniquement les quiz
          filteredEntries = entries.filter((entry) => {
            if (entry.action === "poll_created" && entry.metadata?.pollType) {
              return entry.metadata.pollType === "quizz";
            }
            return true;
          });
        }

        setJournal(filteredEntries);
        setFilteredJournal(filteredEntries);
      } catch (error) {
        logError(
          ErrorFactory.storage(
            "Erreur lors du chargement du journal",
            "Impossible de charger l'historique",
          ),
          {
            component: "ConsumptionJournal",
            metadata: { userId: user?.id, error },
          },
        );
      } finally {
        setLoading(false);
      }
    };

    loadJournal();
  }, [user]);

  // Calculer les mois disponibles depuis le journal
  useEffect(() => {
    const months = new Set<string>();
    journal.forEach((entry) => {
      const month = entry.timestamp.substring(0, 7); // 'YYYY-MM'
      months.add(month);
    });
    const sortedMonths = Array.from(months).sort().reverse();
    setAvailableMonths(sortedMonths);
  }, [journal]);

  // Filtrer par période
  const filteredByPeriod = useMemo(() => {
    if (selectedPeriod === "all") return journal;

    if (selectedPeriod === "current") {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      return journal.filter((entry) => {
        const entryMonth = entry.timestamp.substring(0, 7);
        return entryMonth === currentMonth;
      });
    }

    // Mois spécifique
    return journal.filter((entry) => {
      const entryMonth = entry.timestamp.substring(0, 7);
      return entryMonth === selectedPeriod;
    });
  }, [journal, selectedPeriod]);

  // Filtrer le journal selon la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredJournal(filteredByPeriod);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = filteredByPeriod.filter((entry) => {
      const actionLabel =
        ACTION_LABELS[entry.action]?.label.toLowerCase() || "";
      const metadataStr = JSON.stringify(entry.metadata || {}).toLowerCase();
      return actionLabel.includes(query) || metadataStr.includes(query);
    });

    setFilteredJournal(filtered);
  }, [searchQuery, filteredByPeriod, ACTION_LABELS]);

  // Calculer le label de période
  const periodLabel = useMemo(() => {
    if (selectedPeriod === "all") return t("consumption_journal.period.all");
    if (selectedPeriod === "current")
      return t("consumption_journal.period.current");

    // Mois spécifique - formater en "Janvier 2025"
    const [year, month] = selectedPeriod.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [selectedPeriod, t]);

  // Grouper par date
  const groupedByDate = filteredJournal.reduce(
    (acc, entry) => {
      const date = new Date(entry.timestamp);
      const dateKey = date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(entry);
      return acc;
    },
    {} as Record<string, CreditJournalEntry[]>,
  );

  // Calculer les totaux
  const totalCredits = journal.reduce((sum, entry) => sum + entry.credits, 0);
  const totalByAction = journal.reduce(
    (acc, entry) => {
      acc[entry.action] = (acc[entry.action] || 0) + entry.credits;
      return acc;
    },
    {} as Record<string, number>,
  );

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-[#0a0a0a] flex items-center justify-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${theme.activeBorder.replace("border-", "border-")}`}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-[#0a0a0a] pb-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <TooltipProvider>
          <div className="mb-8">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className={`mb-4 text-gray-400 hover:text-white hover:${theme.bg}`}
                  data-testid="journal-back-dashboard"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t("consumption_journal.back_to_dashboard")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("consumption_journal.back_to_dashboard_tooltip")}</p>
              </TooltipContent>
            </Tooltip>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3 mb-2">
                  <CreditCard className={`w-8 h-8 ${theme.text}`} />
                  {t("consumption_journal.title")}
                </h1>
                <p className="text-gray-400">
                  {t("consumption_journal.subtitle")}
                </p>
                {user && (
                  <div className="text-sm text-gray-500 mt-1">
                    {isSolo
                      ? t("consumption_journal.plan_solo_tag")
                      : isFree
                        ? t("consumption_journal.plan_free_tag")
                        : ""}
                  </div>
                )}
              </div>

              {/* Statistiques globales */}
              <div className="text-right">
                <div className="text-2xl font-bold text-white">
                  {totalCredits}
                </div>
                <div className="text-sm text-gray-400">
                  {t("consumption_journal.credits_consumed")}
                </div>
              </div>
            </div>
          </div>

          {/* Section plan / quotas */}
          <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {isSolo
                    ? t("consumption_journal.plan_solo")
                    : isFree
                      ? t("consumption_journal.plan_free")
                      : t("consumption_journal.your_plan")}
                </h3>
                <p className="text-gray-400">
                  {isSolo
                    ? t("consumption_journal.plan_solo_desc")
                    : isFree
                      ? t("consumption_journal.plan_free_desc")
                      : t("consumption_journal.plan_guest_desc")}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-400">
                  {getResetInfo().nextMonthCredits}
                </div>
                <div className="text-sm text-gray-400">
                  {isSolo
                    ? t("consumption_journal.unlimited")
                    : t("consumption_journal.ai_conversations")}
                </div>
              </div>
            </div>
          </div>
        </TooltipProvider>

        {/* Barre de recherche */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t("consumption_journal.search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#1a1a1a] border-gray-700 text-white"
            />
          </div>

          {/* Sélecteur de période */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <select
              id="period-selector"
              aria-label={t("consumption_journal.select_period")}
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="flex-1 bg-[#1a1a1a] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="current">
                {t("consumption_journal.period.current")}
              </option>
              <option value="all">{t("consumption_journal.period.all")}</option>
              {availableMonths.length > 0 && (
                <optgroup label={t("consumption_journal.specific_months")}>
                  {availableMonths.map((month) => {
                    const [year, monthNum] = month.split("-");
                    const date = new Date(
                      parseInt(year),
                      parseInt(monthNum) - 1,
                    );
                    const label = date.toLocaleDateString("fr-FR", {
                      month: "long",
                      year: "numeric",
                    });
                    return (
                      <option key={month} value={month}>
                        {label}
                      </option>
                    );
                  })}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        {/* Résumé des quotas consommés */}
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t("consumption_journal.quota_summary")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">
                {totalByAction["conversation_created"] || 0}
              </div>
              <div className="text-sm text-gray-400">
                {limits.conversations === 0
                  ? t("consumption_journal.ai_unavailable")
                  : limits.conversations === 999999
                    ? t("consumption_journal.unlimited_feminine")
                    : t("consumption_journal.out_of_conversations", {
                        count: limits.conversations,
                      })}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {totalByAction["poll_created"] || 0}
              </div>
              <div className="text-sm text-gray-400">
                {t("consumption_journal.polls_created")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">
                {totalByAction["ai_message"] || 0}
              </div>
              <div className="text-sm text-gray-400">
                {limits.aiMessages === 0
                  ? t("consumption_journal.messages_unavailable")
                  : t("consumption_journal.out_of_messages", {
                      count: limits.aiMessages,
                    })}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">
                {totalByAction["analytics_query"] || 0}
              </div>
              <div className="text-sm text-gray-400">
                {limits.analytics === 0
                  ? t("consumption_journal.analytics_unavailable")
                  : t("consumption_journal.out_of_analytics", {
                      count: limits.analytics,
                    })}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques par action */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {Object.entries(ACTION_LABELS).map(
            ([action, { label, icon, color, quotaType }]) => {
              const total = totalByAction[action] || 0;
              if (total === 0) return null;

              return (
                <div
                  key={action}
                  className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4"
                >
                  <div className={`flex items-center gap-2 mb-2 ${color}`}>
                    {icon}
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">
                      {quotaType}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-white">{total}</div>
                  <div className="text-xs text-gray-400">
                    {t("consumption_journal.credits_label")}
                  </div>
                </div>
              );
            },
          )}
        </div>

        {/* Liste du journal */}
        {filteredJournal.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-400 mb-2">
              {searchQuery
                ? t("consumption_journal.empty.no_results")
                : t("consumption_journal.empty.no_consumption")}
            </h3>
            <p className="text-gray-500">
              {searchQuery
                ? t("consumption_journal.empty.no_results_hint")
                : t("consumption_journal.empty.no_consumption_hint")}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByDate)
              .sort(([dateA], [dateB]) => {
                // Trier par date (plus récent en premier)
                return new Date(dateB).getTime() - new Date(dateA).getTime();
              })
              .map(([dateKey, entries]) => (
                <div
                  key={dateKey}
                  className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-6"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <h2 className="text-lg font-semibold text-white">
                      {dateKey}
                    </h2>
                    <span className="text-sm text-gray-400">
                      ({entries.length}{" "}
                      {entries.length === 1
                        ? t("consumption_journal.credits_label")
                        : t("consumption_journal.credits_label_plural")}
                      )
                    </span>
                  </div>

                  <div className="space-y-2">
                    {entries.map((entry) => {
                      const actionInfo =
                        ACTION_LABELS[entry.action] || ACTION_LABELS.other;
                      const date = new Date(entry.timestamp);
                      const timeStr = date.toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      });

                      // Déterminer l'icône et le label spécifique pour les polls
                      let displayIcon = actionInfo.icon;
                      let displayLabel = actionInfo.label;

                      if (
                        entry.action === "poll_created" &&
                        entry.metadata?.pollType
                      ) {
                        const pollType = entry.metadata.pollType;
                        displayIcon =
                          POLL_TYPE_ICONS[pollType] || actionInfo.icon;
                        displayLabel =
                          entry.metadata.pollTypeLabel || actionInfo.label;
                      }

                      return (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg border border-gray-800"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={actionInfo.color}>
                              {displayIcon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">
                                  {displayLabel}
                                </span>
                                <span className="text-gray-500 text-sm">
                                  {timeStr}
                                </span>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${actionInfo.color.replace("text", "bg").replace("-400", "/20") + actionInfo.color.replace("text-", " text-")}`}
                                >
                                  {actionInfo.quotaType}
                                </span>
                              </div>
                              {entry.metadata && (
                                <div className="text-xs text-gray-400 mt-1 space-y-1">
                                  {entry.metadata.conversationId && (
                                    <span>
                                      {t(
                                        "consumption_journal.conversation_label",
                                      )}{" "}
                                      {entry.metadata.conversationId.slice(
                                        0,
                                        8,
                                      )}
                                      ...
                                    </span>
                                  )}
                                  {entry.metadata.pollId && (
                                    <div className="flex items-center gap-2">
                                      <span>
                                        {t("consumption_journal.poll_label")}
                                      </span>
                                      <PollLink
                                        pollId={entry.metadata.pollId}
                                        pollType={entry.metadata.pollType}
                                      />
                                      {entry.action === "poll_created" && (
                                        <span
                                          className={`text-xs px-2 py-0.5 rounded ${
                                            entry.metadata.conversationId
                                              ? "bg-purple-900/30 text-purple-300"
                                              : "bg-blue-900/30 text-blue-300"
                                          }`}
                                        >
                                          {entry.metadata.conversationId
                                            ? t("consumption_journal.via_ai")
                                            : t(
                                                "consumption_journal.via_manual",
                                              )}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {entry.metadata.analyticsQuery && (
                                    <span className="ml-2">
                                      {t("consumption_journal.query_label")}{" "}
                                      {entry.metadata.analyticsQuery}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-white">
                              {entry.credits}
                            </div>
                            <div className="text-xs text-gray-400">
                              {entry.credits === 1
                                ? t("consumption_journal.credits_label")
                                : t("consumption_journal.credits_label_plural")}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
